import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  api, styleOf, saleRichieste, SALE, STATO_STYLE, STATO_SALA_STYLE, esamiAttivi,
  openConsenso, todayISO, activeDay, matchPatient, validaCodiceFiscale, logout,
} from "@/lib/clinic";
import { useQueue } from "@/hooks/useQueue";
import { Logo } from "@/components/Logo";
import { AddPatientDialog } from "@/components/AddPatientDialog";
import { DoctorDialog } from "@/components/DoctorDialog";
import { ExcelImportDialog } from "@/components/ExcelImportDialog";
import { PlannerViewDialog } from "@/components/PlannerViewDialog";
import { PatientDetailDialog } from "@/components/PatientDetailDialog";
import { WorkdayPicker } from "@/components/WorkdayPicker";
import { ChatWidget } from "@/components/ChatWidget";
import {
  Printer, Trash2, UserPlus, FileSpreadsheet, Monitor, Wifi, WifiOff, Search, Eye,
  Users, CheckCircle2, UserX, RotateCcw, Megaphone, Stethoscope, Eraser, Pencil, BarChart3, UserCheck, X,
  CalendarDays, AlertTriangle, Undo2, ShieldCheck, LogOut, Bell, Loader2,
} from "lucide-react";

const Hint = ({ label, children }) => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="top" className="font-medium">{label}</TooltipContent>
  </Tooltip>
);

const Kpi = ({ icon: Icon, label, value, tone }) => (
  <div className="relative rounded-xl border border-slate-200 bg-white p-5 overflow-hidden">
    <div className={`absolute left-0 top-0 h-full w-1 ${tone}`} />
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">{value}</div>
      </div>
      <Icon className="w-5 h-5 text-slate-400" />
    </div>
  </div>
);

const CurrentRoomCard = ({ sala, patient, onRipeti, ripetendo }) => (
  <div className="relative rounded-xl border border-slate-200 bg-white p-5 overflow-hidden" data-testid={`dashboard-current-${sala}`}>
    <div className={`absolute left-0 top-0 h-full w-1 ${sala === "levi" ? "bg-violet-500" : "bg-cyan-500"}`} />
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{SALE[sala].label}</div>
        {patient ? (
          <>
            <div className="font-ticket text-3xl font-bold text-slate-900 mt-1">{patient.codice}</div>
            <div className="text-sm text-slate-600 truncate">{patient.nome}</div>
          </>
        ) : (
          <div className="font-display text-2xl text-slate-300 mt-2">Libera</div>
        )}
      </div>
      <Hint label={patient ? `Ripeti l'annuncio vocale di ${patient.codice} in sala d'attesa` : "Nessuna chiamata da ripetere"}>
        <span>
          <Button
            size="sm"
            variant="outline"
            disabled={!patient || ripetendo}
            onClick={onRipeti}
            data-testid={`repeat-call-${sala}-btn`}
            className={`rounded-full h-10 w-10 p-0 ${
              sala === "levi"
                ? "border-violet-200 text-violet-700 hover:bg-violet-50"
                : "border-cyan-200 text-cyan-700 hover:bg-cyan-50"
            }`}
          >
            {ripetendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
          </Button>
        </span>
      </Hint>
    </div>
  </div>
);

export default function Dashboard() {
  const { patients, current, medico, connected, lastEvent } = useQueue();
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [ripetendo, setRipetendo] = useState(null);
  const [bloccate, setBloccate] = useState([]);

  const today = todayISO();
  const conteggioGiorni = useMemo(() => {
    const acc = {};
    patients.forEach((p) => {
      const d = p.data || today;
      acc[d] = (acc[d] || 0) + 1;
    });
    return acc;
  }, [patients, today]);
  const giorni = useMemo(() => Object.keys(conteggioGiorni).sort(), [conteggioGiorni]);
  const [giorno, setGiorno] = useState(today);

  // Nuvolette rapide: solo le ultime 3 giornate con pazienti (per le altre c'è il calendario).
  const giorniChip = useMemo(() => {
    const ultimi = giorni.slice(-3);
    return giorni.includes(giorno) && !ultimi.includes(giorno) ? [giorno, ...ultimi] : ultimi;
  }, [giorni, giorno]);

  // Nessun cambio automatico di giornata mentre una scheda è aperta: si perderebbe il lavoro in corso.
  useEffect(() => {
    if (detail) return;
    if (giorni.length && !giorni.includes(giorno)) setGiorno(activeDay(patients));
  }, [giorni, giorno, patients, detail]);

  useEffect(() => {
    if (!detail) return;
    const fresh = patients.find((p) => p.id === detail.id);
    if (fresh && fresh !== detail) setDetail(fresh);
  }, [patients, detail]);

  const searching = query.trim().length > 0;
  const giornata = useMemo(
    () => patients.filter((p) => (p.data || today) === giorno),
    [patients, giorno, today]
  );

  const base = searching ? patients.filter((p) => matchPatient(p, query)) : giornata;

  const filtered = useMemo(() => {
    if (tab === "da_accettare") return base.filter((p) => !p.accettato);
    if (tab === "levi") return base.filter((p) => p.visita);
    if (tab === "jenner") return base.filter((p) => p.ambulatorio);
    if (tab === "assenti") return base.filter((p) => p.stato === "assente");
    return base;
  }, [base, tab]);

  const stats = useMemo(() => ({
    elenco: giornata.length,
    accettati: giornata.filter((p) => p.accettato && p.stato !== "assente").length,
    completati: giornata.filter((p) => p.stato === "completato").length,
    assenti: giornata.filter((p) => p.stato === "assente").length,
  }), [giornata]);

  const accetta = async (p) => {
    try {
      const { data } = await api.post(`/patients/${p.id}/accetta`);
      toast.success(`${data.nome} accettato — codice ${data.codice} · arrivo ${data.orario_arrivo}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile confermare l'accettazione");
    }
  };

  const call = async (p, sala) => {
    try {
      await api.post(`/patients/${p.id}/call`, null, { params: { sala } });
      toast.success(`${p.codice} chiamato in ${SALE[sala].label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile chiamare il paziente");
    }
  };

  // Pausa di 3 secondi per sala: evita chiamate multiple per click ripetuti.
  const callNext = async (sala) => {
    if (bloccate.includes(sala)) return;
    setBloccate((b) => [...b, sala]);
    setTimeout(() => setBloccate((b) => b.filter((s) => s !== sala)), 3000);
    try {
      const { data } = await api.post("/call-next", null, { params: { sala, data: giorno } });
      toast.success(`${data.codice} chiamato in ${SALE[sala].label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nessun paziente in attesa");
    }
  };

  const complete = async (p, sala) => {
    setBusy(sala ? `${p.id}-${sala}` : p.id);
    try {
      await api.post(`/patients/${p.id}/complete`, null, { params: sala ? { sala } : {} });
      toast.success(sala ? `${p.codice || p.nome} · ${SALE[sala].short} completata` : `${p.codice || p.nome} completato`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile completare");
    } finally {
      setBusy(null);
    }
  };

  const annullaChiamata = async (p) => {
    try {
      await api.post(`/patients/${p.id}/annulla-chiamata`);
      toast.success(`Chiamata di ${p.codice || p.nome} annullata — torna in coda al suo posto`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile annullare la chiamata");
    }
  };

  const absent = async (p) => {
    try {
      await api.post(`/patients/${p.id}/absent`);
      toast.warning(`${p.codice || p.nome} segnato come assente`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile segnare l'assenza");
    }
  };

  const restore = async (p) => {
    try {
      await api.post(`/patients/${p.id}/restore`);
      toast.success(`${p.codice || p.nome} rimesso in attesa`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile ripristinare");
    }
  };

  const riapri = async (p) => {
    try {
      const { data } = await api.post(`/patients/${p.id}/riapri`);
      toast.success(`${data.codice || data.nome} rimesso in coda`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile riaprire la visita");
    }
  };

  const ripetiChiamata = async (sala) => {
    setRipetendo(sala);
    try {
      const { data } = await api.post("/display/ripeti", null, { params: { sala } });
      toast.success(`Annuncio ripetuto in sala: ${data.codice}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile ripetere l'annuncio");
    } finally {
      setRipetendo(null);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    await api.delete(`/patients/${toDelete.id}`);
    toast.success(`${toDelete.codice || toDelete.nome} rimosso`);
    setToDelete(null);
  };

  const svuota = async (tutto) => {
    try {
      const { data } = await api.delete("/patients", { params: tutto ? {} : { data: giorno } });
      toast.success(`${data.deleted} pazienti eliminati`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile svuotare la lista");
    } finally {
      setClearOpen(false);
    }
  };

  const stampa = (p) => openConsenso(p);

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-[1700px] mx-auto px-5 sm:px-8 py-4 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <Logo className="h-8 sm:h-9" />
            <div className="h-9 w-px bg-slate-200" />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900 leading-none">
                Gestione Ambulatorio
              </h1>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-slate-400">
                Accettazione e code
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              data-testid="websocket-connection-status-badge"
              variant="outline"
              className={`gap-1.5 rounded-full ${connected ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-rose-300 text-rose-700 bg-rose-50"}`}
            >
              {connected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
              {connected ? "Live" : "Offline"}
            </Badge>
            <Button
              variant="outline"
              className={`rounded-full ${medico ? "border-sky-300 text-sky-800 bg-sky-50" : "border-amber-300 text-amber-800 bg-amber-50"}`}
              onClick={() => setDoctorOpen(true)}
              data-testid="doctor-select-btn"
            >
              <Stethoscope className="w-4 h-4 mr-2" />
              {medico || "Scegli medico"}
            </Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="nav-calendar-link">
              <Link to="/calendario"><CalendarDays className="w-4 h-4 mr-2" /> Calendario</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="nav-report-link">
              <Link to="/report"><BarChart3 className="w-4 h-4 mr-2" /> Report</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="nav-display-screen-link">
              <Link to="/display" target="_blank"><Monitor className="w-4 h-4 mr-2" /> Schermo Sala</Link>
            </Button>
            <div className="flex items-center">
              <Button
                variant="outline"
                className="rounded-l-full rounded-r-none border-r-0"
                onClick={() => setExcelOpen(true)}
                data-testid="excel-import-open-modal-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Importa Planner
              </Button>
              <Hint label="Visualizza il planner importato">
                <Button
                  variant="outline"
                  className="rounded-r-full rounded-l-none px-3"
                  onClick={() => setPlannerOpen(true)}
                  data-testid="planner-view-btn"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </Hint>
            </div>
            <Button className="rounded-full" onClick={() => setAddOpen(true)} data-testid="add-patient-open-modal-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Nuovo Paziente
            </Button>
            <Hint label="Sicurezza e cambio password">
              <Button asChild variant="outline" size="icon" className="rounded-full" data-testid="nav-security-link">
                <Link to="/impostazioni/sicurezza"><ShieldCheck className="w-4 h-4" /></Link>
              </Button>
            </Hint>
            <Hint label="Esci dal gestionale">
              <Button
                variant="outline"
                size="icon"
                className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"
                onClick={logout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </Hint>
          </div>
        </div>
      </header>

      <main className="max-w-[1700px] mx-auto px-5 sm:px-8 py-7 space-y-7">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Users} label="In elenco" value={stats.elenco} tone="bg-sky-500" />
          <Kpi icon={UserCheck} label="Accettati" value={stats.accettati} tone="bg-emerald-500" />
          <Kpi icon={CheckCircle2} label="Completati" value={stats.completati} tone="bg-slate-400" />
          <Kpi icon={UserX} label="Assenti" value={stats.assenti} tone="bg-rose-500" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {["levi", "jenner"].map((sala) => (
            <div key={sala} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <CurrentRoomCard
                  sala={sala}
                  patient={current?.[sala]}
                  onRipeti={() => ripetiChiamata(sala)}
                  ripetendo={ripetendo === sala}
                />
              </div>
              <Button
                onClick={() => callNext(sala)}
                disabled={bloccate.includes(sala)}
                data-bloccato={bloccate.includes(sala)}
                data-testid={`call-next-${sala}-btn`}
                className={`rounded-xl h-auto py-4 px-5 font-display text-base transition-transform duration-100 active:scale-[0.94] ${
                  bloccate.includes(sala) ? "scale-[0.94] saturate-50" : ""
                } ${sala === "levi" ? "bg-violet-700 hover:bg-violet-800" : "bg-cyan-700 hover:bg-cyan-800"}`}
              >
                <Megaphone className="w-4 h-4 mr-2" /> Chiama prossimo
              </Button>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, cognome, azienda, mansione, codice, CF, esame... (ricerca su tutte le giornate)"
              className="pl-10 pr-10 h-11"
              data-testid="patient-search-input"
            />
            {searching && (
              <button
                onClick={() => setQuery("")}
                data-testid="patient-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <div className="text-xs uppercase tracking-widest text-slate-500">Giornata di lavoro</div>
                <WorkdayPicker
                  value={giorno}
                  onChange={setGiorno}
                  counts={conteggioGiorni}
                  disabled={searching}
                  testId="dashboard-date-picker"
                />
              </div>
              {giorni.length > 1 && !searching && (
                <div className="flex flex-wrap gap-1.5 pb-1" data-testid="dashboard-available-days">
                  {giorniChip.map((d) => (
                    <button
                      key={d}
                      onClick={() => setGiorno(d)}
                      data-testid={`dashboard-day-chip-${d}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        d === giorno ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      {new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} ·{" "}
                      {patients.filter((p) => (p.data || today) === d).length}
                    </button>
                  ))}
                </div>
              )}
              {searching && (
                <div className="pb-2 text-sm text-slate-500" data-testid="search-scope-note">
                  Ricerca su tutto l'archivio · {filtered.length} risultati
                </div>
              )}
            </div>
            <Button
              variant="outline"
              className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"
              onClick={() => setClearOpen(true)}
              data-testid="clear-list-btn"
            >
              <Eraser className="w-4 h-4 mr-2" /> Svuota lista
            </Button>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="prestazione-tabs">
              <TabsTrigger value="all" data-testid="tab-all">Tutti ({base.length})</TabsTrigger>
              <TabsTrigger value="da_accettare" data-testid="tab-da-accettare">
                Da accettare ({base.filter((p) => !p.accettato).length})
              </TabsTrigger>
              <TabsTrigger value="levi" data-testid="tab-levi">
                Levi Montalcini ({base.filter((p) => p.visita).length})
              </TabsTrigger>
              <TabsTrigger value="jenner" data-testid="tab-jenner">
                Jenner ({base.filter((p) => p.ambulatorio).length})
              </TabsTrigger>
              <TabsTrigger value="assenti" data-testid="tab-assenti">
                Assenti ({base.filter((p) => p.stato === "assente").length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1200px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3 font-semibold">Codice</th>
                  <th className="px-4 py-3 font-semibold">Previsto</th>
                  <th className="px-5 py-3 font-semibold">Dipendente</th>
                  <th className="px-5 py-3 font-semibold">Azienda / Mansione</th>
                  <th className="px-5 py-3 font-semibold">Esami</th>
                  <th className="px-5 py-3 font-semibold">Sale</th>
                  <th className="px-5 py-3 font-semibold">Stato</th>
                  <th className="px-5 py-3 font-semibold text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const st = styleOf(p);
                  const sta = STATO_STYLE[p.stato] || STATO_STYLE.da_accettare;
                  const rooms = saleRichieste(p);
                  const esami = esamiAttivi(p);
                  const inCorso = rooms.filter((r) => p.sale?.[r] === "in_corso");
                  return (
                    <tr
                      key={p.id}
                      data-testid="patient-table-row"
                      onClick={() => setDetail(p)}
                      className={`border-b border-slate-100 last:border-0 transition-colors cursor-pointer hover:bg-slate-50/80 ${p.stato === "chiamato" ? "bg-emerald-50/60" : ""} ${p.stato === "assente" ? "bg-rose-50/40" : ""}`}
                    >
                      <td className="px-5 py-3">
                        {p.codice ? (
                          <span className={`inline-flex items-center justify-center min-w-[62px] rounded-lg px-3 py-1.5 font-ticket font-bold text-base ${st.badge}`}>
                            {p.codice}
                          </span>
                        ) : (
                          <span className="text-slate-300 font-ticket text-base">—</span>
                        )}
                        {p.orario_arrivo && (
                          <div className="text-[10px] font-ticket text-slate-400 mt-1">arrivo {p.orario_arrivo}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-testid="patient-orario-previsto-cell">
                        {p.orario ? (
                          <span
                            data-testid="patient-orario-previsto"
                            title="Orario previsto dal planner"
                            className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-ticket text-sm font-bold text-slate-600"
                          >
                            {p.orario}
                          </span>
                        ) : (
                          <span className="font-ticket text-sm text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-900">
                        <div className="flex items-center gap-1.5">
                          {[p.nome, p.cognome].filter(Boolean).join(" ")}
                          {validaCodiceFiscale(p.codice_fiscale) && (
                            <Hint label={`Codice fiscale: ${validaCodiceFiscale(p.codice_fiscale)}`}>
                              <span data-testid="patient-cf-warning" className="text-amber-600">
                                <AlertTriangle className="w-3.5 h-3.5" />
                              </span>
                            </Hint>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-sm text-slate-800">{p.azienda || "—"}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[210px]">{p.mansione || ""}</div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {esami.map((e) => (
                            <span
                              key={e.key}
                              title={`${e.label}: ${e.valore}`}
                              data-testid={`patient-esame-${e.key}`}
                              className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-bold text-slate-700"
                            >
                              {e.key}
                            </span>
                          ))}
                          {esami.length === 0 && <span className="text-xs text-slate-300">—</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {rooms.map((r) => (
                            <span
                              key={r}
                              data-testid={`patient-room-badge-${r}`}
                              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${STATO_SALA_STYLE[p.sale?.[r]] || STATO_SALA_STYLE.in_attesa}`}
                            >
                              {SALE[r].short}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-block rounded-full border px-2.5 py-1 text-xs font-semibold ${sta.cls}`}>
                          {sta.label}
                        </span>
                      </td>
                      <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5 justify-end">
                          <Hint label={p.codice ? "Stampa il consenso con il codice" : "Stampa il consenso: genera subito il codice"}>
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => stampa(p)} data-testid="patient-print-ticket-btn">
                              <Printer className="w-4 h-4 sm:mr-1.5" />
                              <span className="hidden sm:inline">Stampa</span>
                            </Button>
                          </Hint>
                          {!p.accettato ? (
                            <Button
                              size="sm"
                              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => accetta(p)}
                              data-testid="patient-confirm-arrival-btn"
                            >
                              <UserCheck className="w-4 h-4 mr-1.5" /> Conferma accettazione
                            </Button>
                          ) : (
                            <>
                              {rooms.map((r) => (
                                <Hint key={r} label={`Chiama subito in ${SALE[r].label}`}>
                                  <Button
                                    size="sm"
                                    onClick={() => call(p, r)}
                                    data-testid={`patient-call-${r}-btn`}
                                    className={`rounded-full ${r === "levi" ? "bg-violet-700 hover:bg-violet-800" : "bg-cyan-700 hover:bg-cyan-800"}`}
                                  >
                                    <Megaphone className="w-3.5 h-3.5 mr-1.5" />
                                    {SALE[r].short.split(" ")[0]}
                                  </Button>
                                </Hint>
                              ))}
                            </>
                          )}
                          {inCorso.length > 0 && (
                            <Hint label="Chiamata per errore: annulla e rimetti in coda">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="rounded-full text-amber-600 hover:bg-amber-50"
                                onClick={() => annullaChiamata(p)}
                                data-testid="patient-cancel-call-btn"
                              >
                                <Undo2 className="w-4 h-4" />
                              </Button>
                            </Hint>
                          )}
                          {p.stato !== "assente" && rooms.some((r) => p.sale?.[r] === "completato") && (
                            <Hint label="Completato per errore: riapre l'ultima sala e rimette in coda">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="rounded-full text-sky-600 hover:bg-sky-50"
                                onClick={() => riapri(p)}
                                data-testid="patient-reopen-btn"
                              >
                                <Undo2 className="w-4 h-4" />
                              </Button>
                            </Hint>
                          )}
                          <Hint label="Modifica dati e scheda paziente">
                            <Button size="sm" variant="ghost" className="rounded-full text-slate-500" onClick={() => setDetail(p)} data-testid="patient-edit-btn">
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </Hint>
                          {p.stato === "assente" ? (
                            <Hint label="Rimetti in attesa">
                              <Button size="sm" variant="ghost" className="rounded-full text-emerald-700" onClick={() => restore(p)} data-testid="patient-restore-btn">
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            </Hint>
                          ) : (
                            <Hint label="Segna come assente (non presentato)">
                              <Button size="sm" variant="ghost" className="rounded-full text-rose-600 hover:bg-rose-50" onClick={() => absent(p)} data-testid="patient-absent-btn">
                                <UserX className="w-4 h-4" />
                              </Button>
                            </Hint>
                          )}
                          {p.accettato &&
                            rooms
                              .filter((r) => p.sale?.[r] !== "completato")
                              .map((r) => (
                                <Hint key={r} label={`Completa solo ${SALE[r].label}`}>
                                  <button
                                    onClick={() => complete(p, r)}
                                    disabled={busy === `${p.id}-${r}`}
                                    data-testid={`patient-complete-${r}-btn`}
                                    className={`h-7 w-7 rounded-full grid place-items-center transition-transform hover:scale-110 disabled:opacity-40 ${
                                      r === "levi" ? "hover:bg-violet-50" : "hover:bg-cyan-50"
                                    }`}
                                  >
                                    <span
                                      className={`h-3.5 w-3.5 rounded-full ring-2 ring-offset-1 ${
                                        r === "levi" ? "bg-violet-600 ring-violet-200" : "bg-cyan-600 ring-cyan-200"
                                      }`}
                                    />
                                  </button>
                                </Hint>
                              ))}
                          <Hint label="Segna visita completata">
                            <Button size="sm" variant="ghost" className="rounded-full text-slate-500" onClick={() => complete(p)} data-testid="patient-complete-btn">
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          </Hint>                          <Hint label="Elimina dall'elenco">
                            <Button size="sm" variant="ghost" className="rounded-full text-slate-400 hover:text-rose-600" onClick={() => setToDelete(p)} data-testid="patient-delete-btn">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </Hint>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-14 text-center text-slate-400 font-display text-xl" data-testid="empty-state">
                      {searching ? "Nessun paziente trovato" : "Nessun paziente in questa lista"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <AddPatientDialog open={addOpen} onOpenChange={setAddOpen} />
      <ChatWidget lastEvent={lastEvent} />
      <DoctorDialog open={doctorOpen} onOpenChange={setDoctorOpen} selezionato={medico} />
      <ExcelImportDialog open={excelOpen} onOpenChange={setExcelOpen} onImported={(d) => d && setGiorno(d)} />
      <PlannerViewDialog open={plannerOpen} onOpenChange={setPlannerOpen} />
      <PatientDetailDialog patient={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />

      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent data-testid="clear-list-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Svuotare la lista pazienti?</AlertDialogTitle>
            <AlertDialogDescription>
              Puoi eliminare solo la giornata del {new Date(giorno).toLocaleDateString("it-IT")} ({giornata.length} pazienti)
              oppure tutti i {patients.length} pazienti presenti in archivio. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="clear-cancel-btn">Annulla</AlertDialogCancel>
            <Button variant="outline" className="rounded-md" onClick={() => svuota(false)} data-testid="clear-day-btn">
              Svuota giornata
            </Button>
            <AlertDialogAction onClick={() => svuota(true)} data-testid="clear-all-btn" className="bg-rose-600 hover:bg-rose-700">
              Svuota tutto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere {toDelete?.codice || toDelete?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.nome} verrà eliminato dall'elenco.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-btn" className="bg-rose-600 hover:bg-rose-700">
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}
